import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import Modal from "@commons/Modal";
import { usePage } from "@hooks/usePage";
import Heading from "@commons/Typography/Heading";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { AppError } from "@utils/errors/appError";
import ComboBox from "@commons/Input/ComboBox";
import { searchIndividuals } from "@services/individuals/search";
import {
  ComboBoxDefault,
  type ComboBoxOption,
} from "@typings/components/comboBox";
import ListPreview from "@commons/Preview/ListPreview";
import type { Individual } from "@typings/entities/individuals";
import { globalMutate } from "@utils/swr";
import { updateCollarIndividual } from "@services/device/collar/updateIndividual";

const UpdateCollarIndividualSchema = z.object({
  individual: z.object({
    label: z.string().min(1, "Indivíduo é obrigatório"),
    value: z.string().min(1, "Indivíduo é obrigatório"),
  }),
});

type UpdateCollarIndividualFormData = z.infer<
  typeof UpdateCollarIndividualSchema
>;

function ModalUpdateCollarIndividual() {
  const { isModalOpen, closeModal, dataModal, setIsLoading } = usePage();
  const [individualDetails, setIndividualDetails] = useState<Individual | null>(
    null,
  );
  const open = isModalOpen("updateCollarIndividual");

  const collarId = dataModal?.collarId as string;

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
  } = useForm<UpdateCollarIndividualFormData>({
    resolver: zodResolver(UpdateCollarIndividualSchema),
    defaultValues: {
      individual: ComboBoxDefault,
    },
  });

  const selectedIndividual = watch(
    "individual",
  ) as ComboBoxOption<Individual> | null;

  useEffect(() => {
    setIndividualDetails(selectedIndividual?.item || null);
  }, [selectedIndividual]);

  const onSubmit = async (data: UpdateCollarIndividualFormData) => {
    try {
      setIsLoading(true);

      await updateCollarIndividual({
        id: collarId,
        individualId: data.individual.value,
      });

      toast.success("Indivíduo associado com sucesso");
      await globalMutate([`fetch-collar-${collarId}`]);
      handleClose();
    } catch (error) {
      const isAppError = error instanceof AppError;
      const title = isAppError ? error.message : "Erro ao associar indivíduo";
      toast.error(title);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    reset();
    setIndividualDetails(null);
    closeModal();
  };

  if (!open) return null;

  return (
    <Modal
      open={open}
      heading="Associar Indivíduo ao Rastreador"
      onClose={handleClose}
      onConfirm={handleSubmit(onSubmit)}
      confirmLabel="Associar"
      cancelLabel="Cancelar"
      className="max-w-lg"
    >
      <form className="flex flex-col gap-6">
        <Controller
          name="individual"
          control={control}
          render={({ field }) => (
            <ComboBox<Individual>
              {...field}
              value={field.value}
              onChange={(option) => field.onChange(option ?? null)}
              // Filter for individuals without GPS to prevent double-assignment of collars
              searchService={(query) =>
                searchIndividuals(query, { hasGPS: "false" })
              }
              label="Selecione o Indivíduo"
              error={!!errors.individual}
              helperText={errors.individual?.message}
            />
          )}
        />

        {individualDetails && (
          <div className="flex flex-col gap-2">
            <Heading size="sm">Dados do Indivíduo Selecionado</Heading>
            <ListPreview.Root>
              <ListPreview.Row
                label="N° Ficha:"
                value={individualDetails.clinicalRecord}
              />
              <ListPreview.Row
                label="Nome Comum:"
                value={individualDetails.commonName}
              />
              <ListPreview.Row
                label="Nome Científico:"
                value={individualDetails.taxonomicGroup.species}
              />
              <ListPreview.Row
                label="Nome UFU:"
                value={individualDetails.ufuName}
              />
            </ListPreview.Root>
          </div>
        )}
      </form>
    </Modal>
  );
}

export default ModalUpdateCollarIndividual;
